import { useMappingJobSteps } from '@/hooks/useMappingJobSteps'
import { deriveOverallProgress, toStageProgress } from '@/lib/odmStages'
import { cn } from '@/lib/utils'
import { useMemo } from 'react'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
} from '@droidmap/frontend-ui'
import type { DroidMapMetadata } from '@droidmap/shared-service-contract'

import { StageTimeline } from './StageTimeline'

export { StageTimeline } from './StageTimeline'

export interface TaskStageProgressProps {
  /** The task's mapping job id (a photogrammetry task's `referenceId`). */
  mappingJobId: string
  metadata: DroidMapMetadata
  className?: string
}

/**
 * Live per-stage progress **without** any surrounding chrome: fetches the
 * job's steps (polling while it runs) and renders the ordered stage timeline
 * with a derived overall-progress bar, plus the loading / error / no-data
 * states.
 *
 * Split out from {@link TaskStageProgress} so callers that already supply
 * their own container — a modal panel, say — don't end up with a card nested
 * inside a card.
 */
export function TaskStageProgressContent({
  mappingJobId,
  metadata,
  className,
}: TaskStageProgressProps) {
  const {
    data: steps,
    isLoading,
    isError,
  } = useMappingJobSteps(mappingJobId, metadata)

  const stages = useMemo(() => toStageProgress(steps ?? []), [steps])
  const overall = useMemo(() => deriveOverallProgress(steps ?? []), [steps])

  return (
    <div className={cn(className)}>
      {isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner size="md" className="text-accent" />
        </div>
      ) : isError ? (
        <EmptyState
          title="Progress unavailable"
          body="Progress is unavailable for this job right now."
        />
      ) : stages.length === 0 ? (
        <EmptyState
          title="No stage data yet"
          body="No stage data has been recorded for this job yet."
        />
      ) : (
        <StageTimeline stages={stages} overall={overall} />
      )}
    </div>
  )
}

/**
 * Live per-stage progress for a photogrammetry task's ODM mapping job, as a
 * standalone titled card. Use this when the timeline is its own block on a
 * page; inside a container that already provides framing and section
 * headings, use {@link TaskStageProgressContent}.
 */
export function TaskStageProgress({
  mappingJobId,
  metadata,
  className,
}: TaskStageProgressProps) {
  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle>Processing stages</CardTitle>
      </CardHeader>
      <CardContent>
        <TaskStageProgressContent
          mappingJobId={mappingJobId}
          metadata={metadata}
        />
      </CardContent>
    </Card>
  )
}

export default TaskStageProgress
